// Campaign endpoints: /api/v1/admin/platform/growth/* (API_CONTRACT.md › Growth:
// campaigns; backend/src/routes/admin/platform-growth-campaigns.ts).

import { apiFetch } from './client'
import type {
  AudiencePreview,
  Campaign,
  CampaignAudience,
  CampaignWrite,
  GrowthTemplate,
  ISODay,
  RecipientListParams,
  RecipientListResponse,
  ScheduleEmailDetail,
  ScheduleEmailsResponse,
  ScheduleResponse,
  SendingSettingsWrite,
  SendingStatus,
  TemplatePreview,
  TemplateWrite,
  EmailFooter,
  EmailFooterView,
  FollowUpView,
  PreviewAs,
} from './growthCampaignTypes'

const BASE = '/admin/platform/growth'

// ─── Templates ──────────────────────────────────────────────────────────────

export async function listTemplates(signal?: AbortSignal): Promise<GrowthTemplate[]> {
  const r = await apiFetch<{ templates: GrowthTemplate[] }>(`${BASE}/templates`, { signal })
  return r.templates
}

export async function getTemplate(id: number, signal?: AbortSignal): Promise<GrowthTemplate> {
  const r = await apiFetch<{ template: GrowthTemplate }>(`${BASE}/templates/${id}`, { signal })
  return r.template
}

export async function createTemplate(body: TemplateWrite): Promise<GrowthTemplate> {
  const r = await apiFetch<{ template: GrowthTemplate }>(`${BASE}/templates`, { method: 'POST', body })
  return r.template
}

/** 409 template_in_use: a live campaign needs it to keep passing the check. */
export async function updateTemplate(id: number, patch: TemplateWrite): Promise<GrowthTemplate> {
  const r = await apiFetch<{ template: GrowthTemplate }>(`${BASE}/templates/${id}`, { method: 'PATCH', body: patch })
  return r.template
}

/** 409 template_in_use: a campaign uses it. */
export function deleteTemplate(id: number): Promise<void> {
  return apiFetch(`${BASE}/templates/${id}`, { method: 'DELETE' })
}

/** The email as it would go. Sends nothing, stores nothing. */
export function previewTemplate(body: { subject: string; body: string; lead_id?: number; as?: PreviewAs; signer?: string }, signal?: AbortSignal): Promise<TemplatePreview> {
  return apiFetch(`${BASE}/templates/preview`, { method: 'POST', body, signal })
}

// ─── The signature and footer ───────────────────────────────────────────────

export function getEmailFooter(signal?: AbortSignal): Promise<EmailFooterView> {
  return apiFetch(`${BASE}/email-footer`, { signal })
}

export function saveEmailFooter(footer: EmailFooter): Promise<EmailFooterView> {
  return apiFetch(`${BASE}/email-footer`, { method: 'PUT', body: footer })
}

// ─── The follow-up after signup ─────────────────────────────────────────────

export function getFollowUp(signal?: AbortSignal): Promise<FollowUpView> {
  return apiFetch(`${BASE}/signup-follow-up`, { signal })
}

export function saveFollowUp(body: { enabled: boolean; template_id: number; signer: string; delay_minutes?: number }): Promise<FollowUpView> {
  return apiFetch(`${BASE}/signup-follow-up`, { method: 'PUT', body })
}

// ─── Campaigns ──────────────────────────────────────────────────────────────

export async function listCampaigns(signal?: AbortSignal): Promise<Campaign[]> {
  const r = await apiFetch<{ campaigns: Campaign[] }>(`${BASE}/campaigns`, { signal })
  return r.campaigns
}

export async function getCampaign(id: number, signal?: AbortSignal): Promise<Campaign> {
  const r = await apiFetch<{ campaign: Campaign }>(`${BASE}/campaigns/${id}`, { signal })
  return r.campaign
}

export async function createCampaign(body: CampaignWrite): Promise<Campaign> {
  const r = await apiFetch<{ campaign: Campaign }>(`${BASE}/campaigns`, { method: 'POST', body })
  return r.campaign
}

/** 409 campaign_locked: the audience and emails only change on a draft. */
export async function updateCampaign(id: number, patch: CampaignWrite): Promise<Campaign> {
  const r = await apiFetch<{ campaign: Campaign }>(`${BASE}/campaigns/${id}`, { method: 'PATCH', body: patch })
  return r.campaign
}

export function deleteCampaign(id: number): Promise<void> {
  return apiFetch(`${BASE}/campaigns/${id}`, { method: 'DELETE' })
}

/**
 * "Add to schedule": the owner's approval of every email the campaign plans,
 * follow-ups too. 422 campaign_not_ready with `details.problems`.
 */
export async function startCampaign(id: number): Promise<Campaign> {
  const r = await apiFetch<{ campaign: Campaign }>(`${BASE}/campaigns/${id}/start`, { method: 'POST' })
  return r.campaign
}

export async function pauseCampaign(id: number): Promise<Campaign> {
  const r = await apiFetch<{ campaign: Campaign }>(`${BASE}/campaigns/${id}/pause`, { method: 'POST' })
  return r.campaign
}

export async function resumeCampaign(id: number): Promise<Campaign> {
  const r = await apiFetch<{ campaign: Campaign }>(`${BASE}/campaigns/${id}/resume`, { method: 'POST' })
  return r.campaign
}

/** Leads picked on the Leads page join a draft's hand-picked list. 409 campaign_locked once it's scheduled. */
export function addLeadsToCampaign(id: number, leadIds: number[]): Promise<{ added: number; total: number; campaign: Campaign }> {
  return apiFetch(`${BASE}/campaigns/${id}/add-leads`, { method: 'POST', body: { lead_ids: leadIds } })
}

export function previewAudience(audience: CampaignAudience, signal?: AbortSignal): Promise<AudiencePreview> {
  return apiFetch(`${BASE}/campaigns/audience-preview`, { method: 'POST', body: { audience }, signal })
}

export function campaignRecipients(id: number, params: RecipientListParams = {}, signal?: AbortSignal): Promise<RecipientListResponse> {
  return apiFetch(`${BASE}/campaigns/${id}/recipients`, { query: { ...params }, signal })
}

// ─── The Schedule ───────────────────────────────────────────────────────────

export interface ScheduleRange {
  from?: ISODay
  to?: ISODay
}

/** Each day's counts and the replies to review. No range: the month of today, as whole weeks. */
export function getSchedule(range: ScheduleRange = {}, signal?: AbortSignal): Promise<ScheduleResponse> {
  return apiFetch(`${BASE}/schedule`, { query: { ...range }, signal })
}

/** Every email on those days, in order. A day's list is from = to. */
export function scheduleEmails(range: Required<ScheduleRange>, signal?: AbortSignal): Promise<ScheduleEmailsResponse> {
  return apiFetch(`${BASE}/schedule/emails`, { query: { ...range }, signal })
}

const SCHEDULE_PATH = { campaign: 'sends', one_off: 'one-offs' } as const

/** One email: its subject and its whole body. */
export async function openScheduleEmail(kind: keyof typeof SCHEDULE_PATH, id: number, signal?: AbortSignal): Promise<ScheduleEmailDetail> {
  const r = await apiFetch<{ email: ScheduleEmailDetail }>(`${BASE}/schedule/${SCHEDULE_PATH[kind]}/${id}`, { signal })
  return r.email
}

/** Only one that hasn't gone. 409 not_scheduled otherwise. */
export async function removeScheduleEmail(kind: keyof typeof SCHEDULE_PATH, id: number): Promise<ScheduleEmailDetail> {
  const r = await apiFetch<{ email: ScheduleEmailDetail }>(`${BASE}/schedule/${SCHEDULE_PATH[kind]}/${id}/remove`, { method: 'POST' })
  return r.email
}

/** To another day; a one-off keeps its time of day unless `time` (HH:MM, the Ops zone) says otherwise. */
export async function moveScheduleEmail(kind: keyof typeof SCHEDULE_PATH, id: number, body: { day: ISODay; time?: string }): Promise<ScheduleEmailDetail> {
  const r = await apiFetch<{ email: ScheduleEmailDetail }>(`${BASE}/schedule/${SCHEDULE_PATH[kind]}/${id}/move`, { method: 'POST', body })
  return r.email
}

export function markReviewed(eventId: number): Promise<{ reviewed: true }> {
  return apiFetch(`${BASE}/inbox-events/${eventId}/review`, { method: 'POST' })
}

// ─── Sending ────────────────────────────────────────────────────────────────

export function getSending(signal?: AbortSignal): Promise<SendingStatus> {
  return apiFetch(`${BASE}/sending`, { signal })
}

export function updateSending(patch: SendingSettingsWrite): Promise<SendingStatus> {
  return apiFetch(`${BASE}/sending`, { method: 'PATCH', body: patch })
}

// ─── Replies ────────────────────────────────────────────────────────────────

/** The fallback for a reply the inbox reader didn't match. 409 do_not_contact. */
export function markLeadReplied(leadId: number): Promise<{ replied: true }> {
  return apiFetch(`${BASE}/leads/${leadId}/mark-replied`, { method: 'POST' })
}
